import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { invalidateCategoriesCache } from "../lib/useCategories";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Calendar, X, Check } from "lucide-react";
import { CategoryIcon } from "./CategoryIcon";

export const AdminCategoriesPanel = () => {
  const [cats, setCats]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCat, setEditCat] = useState(null); // catégorie en cours d'édition (modal date)

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/categories");
      setCats(r.data?.categories || []);
    } catch (e) {
      toast.error("Chargement échoué : " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (c) => {
    try {
      await api.post("/admin/categories/toggle", { key: c.key, active: !c.is_active });
      invalidateCategoriesCache();
      toast.success(`${c.label} ${c.is_active ? "désactivée" : "activée"} ✨`);
      load();
    } catch (e) {
      toast.error("Échec : " + (e?.response?.data?.detail || e.message));
    }
  };

  const setComingSoon = async (key, when) => {
    try {
      await api.post("/admin/categories/coming-soon", { key, when });
      invalidateCategoriesCache();
      toast.success(when ? `Date "${when}" enregistrée` : 'Date "Bientôt" supprimée');
      setEditCat(null);
      load();
    } catch (e) {
      toast.error("Échec : " + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12 text-white/40">
      <Loader2 className="animate-spin mr-2" /> Chargement...
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-categories-panel">
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4">
        <h3 className="font-bold text-lg mb-1">Catégories de talents</h3>
        <p className="text-xs text-white/50">
          Active/désactive une catégorie. Les artistes liés à une catégorie désactivée sont automatiquement
          masqués du site public (ils réapparaissent dès que tu réactives). Une catégorie marquée
          « Bientôt » s&apos;affiche avec un badge et une date sur la page d&apos;accueil.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cats.map(c => (
          <div key={c.key}
               className={`rounded-2xl p-4 border ${c.is_active ? "bg-white/[0.04] border-white/10" : "bg-zinc-900/60 border-amber-500/30"}`}
               data-testid={`cat-card-${c.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3">
                <CategoryIcon catKey={c.key} emoji={c.emoji} size={56} />
                <div>
                  <div className="font-bold text-base">{c.label}</div>
                  <div className="text-[10px] text-white/40 uppercase">{c.key}</div>
                </div>
              </div>
              <button onClick={() => toggle(c)}
                      data-testid={`cat-toggle-${c.key}`}
                      className={`px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 ${c.is_active ? "bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-200" : "bg-red-500/20 hover:bg-red-500/40 text-red-200"}`}>
                {c.is_active ? <><Eye size={12}/> Active</> : <><EyeOff size={12}/> Masquée</>}
              </button>
            </div>

            <div className="mt-3 flex items-center justify-between">
              <div className="text-xs">
                {c.coming_soon ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200">
                    <Calendar size={10}/> Bientôt · {c.coming_soon}
                  </span>
                ) : (
                  <span className="text-white/30">Pas de date &laquo;Bientôt&raquo;</span>
                )}
              </div>
              <button onClick={() => setEditCat(c)}
                      data-testid={`cat-edit-date-${c.key}`}
                      className="text-[11px] text-cyan-300 hover:underline">
                {c.coming_soon ? "Modifier date" : "+ Ajouter date"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {editCat && (
        <ComingSoonModal cat={editCat} onClose={() => setEditCat(null)} onSave={setComingSoon} />
      )}
    </div>
  );
};

const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

const ComingSoonModal = ({ cat, onClose, onSave }) => {
  const now = new Date();
  const [month, setMonth] = useState(MONTHS[now.getMonth()]);
  const [year, setYear]   = useState(now.getFullYear() + (now.getMonth() >= 9 ? 1 : 0));
  const [busy, setBusy]   = useState(false);

  // Pré-remplir si la cat a déjà une date au format "Mois YYYY"
  useEffect(() => {
    if (!cat.coming_soon) return;
    const parts = cat.coming_soon.split(" ");
    if (parts.length === 2 && MONTHS.includes(parts[0]) && /^\d{4}$/.test(parts[1])) {
      setMonth(parts[0]); setYear(parseInt(parts[1], 10));
    }
  }, [cat.coming_soon]);

  const save = async () => {
    setBusy(true);
    await onSave(cat.key, `${month} ${year}`);
    setBusy(false);
  };
  const clear = async () => {
    setBusy(true);
    await onSave(cat.key, null);
    setBusy(false);
  };

  const years = [now.getFullYear(), now.getFullYear()+1, now.getFullYear()+2];

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="cat-modal">
      <div className="bg-zinc-900 border border-white/15 rounded-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2">
            <CategoryIcon catKey={cat.key} emoji={cat.emoji} size={32} /> {cat.label} — Bientôt disponible
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="cat-modal-close"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-white/60">
            Choisis la date prévisionnelle. La catégorie restera visible sur la page d&apos;accueil avec un badge
            « Bientôt · {month} {year} ». Pour la rendre cliquable, active aussi la catégorie.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-white/40">Mois</label>
              <select value={month} onChange={e => setMonth(e.target.value)} data-testid="cat-modal-month"
                      className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm">
                {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-white/40">Année</label>
              <select value={year} onChange={e => setYear(parseInt(e.target.value,10))} data-testid="cat-modal-year"
                      className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm">
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={busy} data-testid="cat-modal-save"
                    className="flex-1 px-3 py-2 rounded-xl bg-gradient-to-r from-brand-red to-brand-orange hover:opacity-90 disabled:opacity-50 font-semibold text-sm flex items-center justify-center gap-2">
              <Check size={14}/> Enregistrer
            </button>
            {cat.coming_soon && (
              <button onClick={clear} disabled={busy} data-testid="cat-modal-clear"
                      className="px-3 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/40 text-red-200 text-sm">
                Effacer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
